import { supabase } from "../../supabaseClient";
import extractNameEvidence from "./nameEvidence";
import { resolveEntity } from "./evidenceResolver";
import { resolveImageUrl } from "../mediaUrl";

/**
 * ============================================================
 * HOBBLIVISION MATCHER CANDIDATE RETRIEVAL V3
 * ============================================================
 *
 * Purpose:
 *
 *   Retrieve a useful candidate pool for the matcher from:
 *
 *     1. Canonical items
 *     2. Public user_items
 *
 * Then resolve OCR evidence against the actual database values
 * using the generic evidenceResolver.
 *
 * Architecture:
 *
 *   Parser
 *      ↓
 *   Name Evidence
 *      ↓
 *   Retrieval Aliases
 *      ↓
 *   Database Candidate Retrieval
 *      ↓
 *   Evidence Resolver
 *      ↓
 *   Candidate Evidence
 *      ↓
 *   Matcher
 *
 * IMPORTANT:
 *
 *   Retrieval does NOT decide the final match.
 *
 *   evidenceResolver compares noisy observations against actual
 *   database values after candidates have been retrieved.
 *
 * This intentionally avoids entity-specific OCR correction rules.
 *
 * ============================================================
 */

/**
 * ============================================================
 * CONFIGURATION
 * ============================================================
 */

const MAX_CANONICAL_RESULTS = 25;
const MAX_USER_ITEM_RESULTS = 50;

const MAX_RETRIEVAL_NAMES = 8;
const MAX_RETRIEVAL_NAME_TOKENS = 4;

const MAX_PRIMARY_NAME_ALTERNATIVES = 5;

const MIN_NAME_CONFIDENCE = 0.45;

const MAX_YEAR_FALLBACK_RESULTS = 25;
const MAX_SET_FALLBACK_RESULTS = 25;
const MAX_CARD_NUMBER_FALLBACK_RESULTS = 25;

const MIN_RESOLVER_CANDIDATE_SCORE = 0.45;
const MIN_RETRIEVAL_IDENTITY_SCORE = 0.40;


function resolveCanonicalStockImage(item) {
    if (!item?.stock_image_object_key) return null;

    return resolveImageUrl({
        storage_provider: item.stock_image_storage_provider,
        storage_bucket: item.stock_image_storage_bucket,
        object_key: item.stock_image_object_key,
    });
}

/**
 * ============================================================
 * NORMALIZATION
 * ============================================================
 */

function normalize(value) {
    return String(value || "")
        .toUpperCase()
        .replace(/[^\w\s'-]/g, " ")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function normalizeNameForLookup(value) {
    return String(value || "")
        .toUpperCase()
        .replace(/[^\w\s.'-]/g, " ")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Remove obvious OCR framing noise around an otherwise useful
 * identity.
 *
 * This is intentionally structural rather than semantic.
 *
 * We are NOT saying:
 *
 *   OO = bad
 *   DDUNZE = ODUNZE
 *
 * We are simply avoiding isolated one-character tokens from
 * becoming the entire database query.
 */
function sanitizeRetrievalName(value) {
    const cleaned = normalize(value);

    if (!cleaned) {
        return "";
    }

    let tokens = cleaned
        .split(" ")
        .filter(Boolean);

    while (
        tokens.length > 1 &&
        tokens[0].length === 1
    ) {
        tokens = tokens.slice(1);
    }

    while (
        tokens.length > 1 &&
        tokens[tokens.length - 1].length === 1
    ) {
        tokens = tokens.slice(0, -1);
    }

    return tokens.join(" ").trim();
}

/**
 * Generate conservative variants for database retrieval.
 *
 * These are retrieval aliases only.
 *
 * They do NOT establish identity.
 */
function getRetrievalNameVariants(value) {
    const variants = new Set();

    const normalized = normalize(value);
    const punctuated = normalizeNameForLookup(value);
    const sanitized = sanitizeRetrievalName(value);

    if (punctuated) {
        variants.add(punctuated);
    }

    if (normalized) {
        variants.add(normalized);
    }

    if (sanitized) {
        variants.add(sanitized);
    }

    if (normalized) {
        const tokens = normalized
            .split(" ")
            .filter(Boolean);

        /**
         * Preserve first/last tokens while allowing isolated
         * middle initials to disappear.
         */
        const withoutMiddleInitials =
            tokens.filter(
                (token, index) =>
                    token.length > 1 ||
                    index === 0 ||
                    index === tokens.length - 1
            );

        if (
            withoutMiddleInitials.length !==
            tokens.length
        ) {
            variants.add(
                withoutMiddleInitials.join(" ")
            );
        }
    }

    return [...variants];
}

function isUsableRetrievalName(value) {
    const name =
        sanitizeRetrievalName(value);

    if (!name || name.length < 3) {
        return false;
    }

    const tokens =
        name.split(" ").filter(Boolean);

    if (!tokens.length) {
        return false;
    }

    /**
     * Single-token names are allowed, but only when they
     * contain enough information to be useful as a query.
     */
    if (tokens.length === 1) {
        return tokens[0].length >= 4;
    }

    const meaningfulTokens =
        tokens.filter(
            (token) => token.length > 1
        );

    if (meaningfulTokens.length < 2) {
        return false;
    }

    const usefulTokens =
        meaningfulTokens.filter(
            (token) => token.length >= 3
        );

    return (
        usefulTokens.length >= 2 ||
        (
            meaningfulTokens.length >= 2 &&
            meaningfulTokens.some(
                (token) => token.length >= 4
            )
        )
    );
}

/**
 * ============================================================
 * RETRIEVAL TOKEN EXTRACTION
 * ============================================================
 *
 * This is the important new fallback.
 *
 * Example:
 *
 *   "OO ROME DDUNZE"
 *
 * may produce:
 *
 *   ROME DDUNZE
 *   ROME
 *   DDUNZE
 *
 * But we also preserve the strongest complete-name query:
 *
 *   ROME DDUNZE
 *
 * The token fallback exists because the database may contain:
 *
 *   Rome Odunze
 *
 * even though the noisy OCR string cannot be directly matched
 * with ILIKE.
 *
 * The resolver makes the actual identity decision later.
 */

function getMeaningfulRetrievalTokens(values) {
    const tokens = [];

    for (const value of values || []) {
        const normalized =
            sanitizeRetrievalName(value);

        if (!normalized) {
            continue;
        }

        for (
            const token of normalized
                .split(" ")
                .filter(Boolean)
        ) {
            if (token.length < 4) {
                continue;
            }

            if (
                !tokens.some(
                    (existing) =>
                        normalize(existing) ===
                        normalize(token)
                )
            ) {
                tokens.push(token);
            }
        }
    }

    /**
     * Prefer longer tokens because they provide more
     * discriminating retrieval information.
     */
    return tokens
        .sort(
            (a, b) =>
                b.length - a.length
        )
        .slice(
            0,
            MAX_RETRIEVAL_NAME_TOKENS
        );
}

/**
 * ============================================================
 * POSTGREST HELPERS
 * ============================================================
 */

function escapeLike(value) {
    return String(value || "")
        .replace(/\\/g, "\\\\")
        .replace(/%/g, "\\%")
        .replace(/_/g, "\\_");
}

function buildOrFilter(fields, values) {
    const filters = [];

    for (const value of values || []) {
        const escaped =
            escapeLike(value);

        if (!escaped) {
            continue;
        }

        for (const field of fields) {
            filters.push(
                `${field}.ilike.%${escaped}%`
            );
        }
    }

    return filters.join(",");
}

/**
 ============================================================
 * PARSER CLUE EXTRACTION
 * ============================================================
 */

function getYear(parsedOCR) {
    return (
        parsedOCR?.year ||
        parsedOCR?.cardYear ||
        parsedOCR?.extractedYear ||
        null
    );
}

function getSet(parsedOCR) {
    return (
        parsedOCR?.setName ||
        parsedOCR?.set ||
        parsedOCR?.setClue ||
        null
    );
}

function getBrand(parsedOCR) {
    return (
        parsedOCR?.brand ||
        parsedOCR?.brandClue ||
        null
    );
}

function getCardNumber(parsedOCR) {
    return (
        parsedOCR?.cardNumber ||
        parsedOCR?.card_number ||
        parsedOCR?.number ||
        null
    );
}

function getSearchText(parsedOCR) {
    return String(
        parsedOCR?.correctedText ||
        parsedOCR?.normalizedText ||
        parsedOCR?.combinedText ||
        ""
    );
}

/**
 * ============================================================
 * DEFENSIVE FALLBACK IDENTIFIERS
 * ============================================================
 */

function getFallbackYear(parsedOCR) {
    const structuredYear =
        getYear(parsedOCR);

    if (structuredYear) {
        const numericYear =
            Number(structuredYear);

        if (
            Number.isInteger(numericYear) &&
            numericYear >= 1800 &&
            numericYear <= 2100
        ) {
            return numericYear;
        }
    }

    const text =
        getSearchText(parsedOCR);

    const match =
        text.match(
            /\b(18|19|20)\d{2}\b/
        );

    return match
        ? Number(match[0])
        : null;
}

function getFallbackSet(parsedOCR) {
    const structuredSet =
        getSet(parsedOCR);

    if (structuredSet) {
        return normalize(structuredSet);
    }

    const text =
        getSearchText(parsedOCR);

    /**
     * Conservative format fallback.
     *
     * This is still only a retrieval clue.
     */
    const match =
        text.match(
            /\b(?:18|19|20)\d{2}\s+([A-Z][A-Z0-9'&.-]*(?:\s+[A-Z0-9'&.-]+){0,3})\s+#\s*[A-Z0-9-]+\b/i
        );

    if (!match) {
        return null;
    }

    return normalize(match[1]);
}

function getFallbackCardNumber(parsedOCR) {
    const structuredNumber =
        getCardNumber(parsedOCR);

    if (structuredNumber) {
        return normalize(structuredNumber);
    }

    const text =
        getSearchText(parsedOCR);

    const match =
        text.match(
            /#\s*([A-Z0-9-]+)/i
        );

    return match
        ? normalize(match[1])
        : null;
}

/**
 * ============================================================
 * NAME EVIDENCE → RETRIEVAL ALIASES
 * ============================================================
 *
 * nameEvidence remains the observation/evidence extractor.
 *
 * evidenceResolver is NOT used here to resolve identity because
 * we do not have database values yet.
 */

function getRetrievalNames(
    parsedOCR,
    nameEvidence
) {
    const values = [
        nameEvidence?.primary?.value,

        ...(nameEvidence?.evidence || [])
            .filter(
                (item) =>
                    item?.confidence >=
                    MIN_NAME_CONFIDENCE
            )
            .slice(
                0,
                MAX_PRIMARY_NAME_ALTERNATIVES
            )
            .map(
                (item) => item?.value
            ),

        parsedOCR?.player,
        parsedOCR?.itemName,

        ...(parsedOCR?.nameCandidates || [])
            .slice(
                0,
                MAX_PRIMARY_NAME_ALTERNATIVES
            )
            .map(
                (item) =>
                    typeof item === "string"
                        ? item
                        : item?.value
            ),
    ];

    const normalized = [];

    for (const value of values) {
        if (!value) {
            continue;
        }

        const variants =
            getRetrievalNameVariants(value);

        for (const name of variants) {
            if (
                !isUsableRetrievalName(name)
            ) {
                continue;
            }

            if (
                !normalized.includes(name)
            ) {
                normalized.push(name);
            }
        }
    }

    return normalized.slice(
        0,
        MAX_RETRIEVAL_NAMES
    );
}

/**
 * ============================================================
 * CANDIDATE NORMALIZATION
 * ============================================================
 */

function addCandidates(
    target,
    seen,
    items,
    sourceType,
    retrievalEvidence = []
) {
    for (const item of items || []) {
        if (!item?.id) {
            continue;
        }

        const normalizedItem =
            sourceType === "user_item" &&
            item.item_id &&
            item.item
                ? {
                      ...item,
                      name: item.item.name ?? null,
                      brand: item.item.brand ?? null,
                      set_name: item.item.set_name ?? null,
                      year: item.item.year ?? null,
                      card_number: item.item.card_number ?? null,
                      category_id: item.item.category_id ?? item.category_id ?? null,
                      subcategory_id:
                          item.item.subcategory_id ?? item.subcategory_id ?? null,
                  }
                : item;

        const key =
            `${sourceType}:${normalizedItem.id}`;

        const existingIndex =
            target.findIndex(
                (candidate) =>
                    candidate._candidateKey ===
                    key
            );

        if (existingIndex >= 0) {
            const existing =
                target[existingIndex];

            const mergedEvidence = [
                ...(existing.retrievalEvidence || []),
                ...retrievalEvidence,
            ];

            target[existingIndex] = {
                ...existing,

                retrievalEvidence: [
                    ...new Set(
                        mergedEvidence
                    ),
                ],
            };

            continue;
        }

        if (seen.has(key)) {
            continue;
        }

        seen.add(key);

        target.push({
            ...normalizedItem,

            source: sourceType,
            sourceType,

            retrievalEvidence: [
                ...new Set(
                    retrievalEvidence
                ),
            ],

            _candidateKey: key,
        });
    }
}

function cleanCandidates(
    candidates
) {
    return (candidates || []).map(
        ({
            _candidateKey,
            ...candidate
        }) => candidate
    );
}

/**
 * ============================================================
 * DATABASE FIELD CONFIGURATION
 * ============================================================
 */

const CANONICAL_NAME_FIELDS = [
    "name",
    "additional_details->>Identifier",
    "additional_details->>Team",
];

const USER_ITEM_NAME_FIELDS = [
    "user_specific_name",
    "description",
    "attributes->>parallel",
    "attributes->>card_number",
];

/**
 * Public provisional rows are allowed to contribute identity evidence only
 * while they are actively awaiting review. Denied identities are intentionally
 * excluded so HobbliVision does not learn from data an admin rejected.
 */
function publicProvisionalUserItemsQuery() {
    return supabase
        .from("user_items")
        .select("*, item:items(*)")
        .eq("is_private", false)
        .is("item_id", null)
        .eq("status", "Pending");
}

/**
 * Linked public copies may contribute images/copy evidence, but their identity
 * is canonical and comes from items through item_id.
 */
function publicLinkedUserItemsQuery() {
    return supabase
        .from("user_items")
        .select("*, item:items(*)")
        .eq("is_private", false)
        .not("item_id", "is", null)
        .eq("status", "Approved");
}

/**
 * ============================================================
 * CANONICAL RETRIEVAL
 * ============================================================
 */

async function fetchCanonicalIdentityCandidates(
    parsedOCR,
    names,
    retrievalTokens
) {
    const results = [];
    const seen = new Set();

    const year =
        getFallbackYear(parsedOCR);

    const set =
        getFallbackSet(parsedOCR);

    const cardNumber =
        getFallbackCardNumber(parsedOCR);

    const nameFilter =
        buildOrFilter(
            CANONICAL_NAME_FIELDS,
            names
        );

    /**
     * --------------------------------------------------------
     * NAME + YEAR
     * --------------------------------------------------------
     */

    if (nameFilter && year) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .or(nameFilter)
            .eq("year", year)
            .limit(
                MAX_CANONICAL_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical name+year retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["name", "year"]
            );
        }
    }

    /**
     * --------------------------------------------------------
     * NAME + SET
     * --------------------------------------------------------
     */

    if (nameFilter && set) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .or(nameFilter)
            .ilike(
                "set_name",
                `%${escapeLike(set)}%`
            )
            .limit(
                MAX_CANONICAL_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical name+set retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["name", "set"]
            );
        }
    }

    /**
     * --------------------------------------------------------
     * NAME + YEAR + SET
     * --------------------------------------------------------
     */

    if (
        nameFilter &&
        year &&
        set
    ) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .or(nameFilter)
            .eq("year", year)
            .ilike(
                "set_name",
                `%${escapeLike(set)}%`
            )
            .limit(
                MAX_CANONICAL_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical name+year+set retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["name", "year", "set"]
            );
        }
    }

    /**
     * --------------------------------------------------------
     * NAME + CARD NUMBER
     * --------------------------------------------------------
     */

    if (
        nameFilter &&
        cardNumber
    ) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .or(nameFilter)
            .ilike(
                "card_number",
                `%${escapeLike(cardNumber)}%`
            )
            .limit(
                MAX_CANONICAL_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical name+card-number retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["name", "cardNumber"]
            );
        }
    }

    /**
     * --------------------------------------------------------
     * TOKEN FALLBACK
     * --------------------------------------------------------
     *
     * This is what allows:
     *
     *   OO ROME DDUNZE
     *
     * to retrieve records containing:
     *
     *   ODUNZE
     *
     * even when the entire noisy OCR string does not match.
     */

    if (
        !results.length &&
        retrievalTokens.length
    ) {
        const tokenFilter =
            buildOrFilter(
                CANONICAL_NAME_FIELDS,
                retrievalTokens
            );

        if (tokenFilter) {
            const {
                data,
                error,
            } = await supabase
                .from("items")
                .select("*")
                .or(tokenFilter)
                .limit(
                    MAX_CANONICAL_RESULTS
                );

            if (error) {
                console.warn(
                    "[HobbliVision] Canonical token retrieval failed:",
                    error
                );
            } else {
                addCandidates(
                    results,
                    seen,
                    data,
                    "canonical",
                    ["nameToken"]
                );
            }
        }
    }

    return results;
}

async function fetchCanonicalFallbackCandidates(
    parsedOCR
) {
    const results = [];
    const seen = new Set();

    const year =
        getFallbackYear(parsedOCR);

    const set =
        getFallbackSet(parsedOCR);

    const cardNumber =
        getFallbackCardNumber(parsedOCR);

    /**
     * SET
     */

    if (set) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .ilike(
                "set_name",
                `%${escapeLike(set)}%`
            )
            .limit(
                MAX_SET_FALLBACK_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical set fallback failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["set"]
            );
        }
    }

    /**
     * YEAR
     */

    if (year) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .eq("year", year)
            .limit(
                MAX_YEAR_FALLBACK_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical year fallback failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["year"]
            );
        }
    }

    /**
     * CARD NUMBER
     */

    if (cardNumber) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .ilike(
                "card_number",
                `%${escapeLike(cardNumber)}%`
            )
            .limit(
                MAX_CARD_NUMBER_FALLBACK_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical card-number fallback failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["cardNumber"]
            );
        }
    }

    return results;
}

async function fetchCanonicalNameOnlyCandidates(
    names,
    retrievalTokens
) {
    const results = [];
    const seen = new Set();

    const nameFilter =
        buildOrFilter(
            CANONICAL_NAME_FIELDS,
            names
        );

    if (nameFilter) {
        const {
            data,
            error,
        } = await supabase
            .from("items")
            .select("*")
            .or(nameFilter)
            .limit(
                MAX_CANONICAL_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] Canonical name-only retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "canonical",
                ["nameOnly"]
            );
        }
    }

    /**
     * Token retrieval runs even if name retrieval found nothing.
     */
    if (
        !results.length &&
        retrievalTokens.length
    ) {
        const tokenFilter =
            buildOrFilter(
                CANONICAL_NAME_FIELDS,
                retrievalTokens
            );

        if (tokenFilter) {
            const {
                data,
                error,
            } = await supabase
                .from("items")
                .select("*")
                .or(tokenFilter)
                .limit(
                    MAX_CANONICAL_RESULTS
                );

            if (error) {
                console.warn(
                    "[HobbliVision] Canonical token-only retrieval failed:",
                    error
                );
            } else {
                addCandidates(
                    results,
                    seen,
                    data,
                    "canonical",
                    ["nameToken"]
                );
            }
        }
    }

    return results;
}

async function fetchCanonicalCandidates(
    parsedOCR,
    names,
    retrievalTokens
) {
    const [
        identityCandidates,
        fallbackCandidates,
        nameOnlyCandidates,
    ] = await Promise.all([
        fetchCanonicalIdentityCandidates(
            parsedOCR,
            names,
            retrievalTokens
        ),

        fetchCanonicalFallbackCandidates(
            parsedOCR
        ),

        fetchCanonicalNameOnlyCandidates(
            names,
            retrievalTokens
        ),
    ]);

    const results = [];
    const seen = new Set();

    addCandidates(
        results,
        seen,
        identityCandidates,
        "canonical"
    );

    addCandidates(
        results,
        seen,
        fallbackCandidates,
        "canonical"
    );

    addCandidates(
        results,
        seen,
        nameOnlyCandidates,
        "canonical"
    );

    return results;
}

/**
 * ============================================================
 * USER ITEM RETRIEVAL
 * ============================================================
 */

async function fetchUserItemIdentityCandidates(
    parsedOCR,
    names,
    retrievalTokens
) {
    const results = [];
    const seen = new Set();

    const year =
        getFallbackYear(parsedOCR);

    const set =
        getFallbackSet(parsedOCR);

    const cardNumber =
        getFallbackCardNumber(parsedOCR);

    const nameFilter =
        buildOrFilter(
            USER_ITEM_NAME_FIELDS,
            names
        );

    /**
     * NAME + YEAR
     */

    if (
        nameFilter &&
        year
    ) {
        const {
            data,
            error,
        } = await publicProvisionalUserItemsQuery()
            .or(nameFilter)
            .eq(
                "user_specific_year",
                year
            )
            .limit(
                MAX_USER_ITEM_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] User-item name+year retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "user_item",
                ["name", "year"]
            );
        }
    }

    /**
     * NAME + SET
     */

    if (
        nameFilter &&
        set
    ) {
        const {
            data,
            error,
        } = await publicProvisionalUserItemsQuery()
            .or(nameFilter)
            .ilike(
                "user_specific_set_name",
                `%${escapeLike(set)}%`
            )
            .limit(
                MAX_USER_ITEM_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] User-item name+set retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "user_item",
                ["name", "set"]
            );
        }
    }

    /**
     * NAME + CARD NUMBER
     */

    if (
        nameFilter &&
        cardNumber
    ) {
        const {
            data,
            error,
        } = await publicProvisionalUserItemsQuery()
            .or(nameFilter)
            .filter(
                "attributes->>card_number",
                "ilike",
                `%${escapeLike(cardNumber)}%`
            )
            .limit(
                MAX_USER_ITEM_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] User-item name+card-number retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "user_item",
                ["name", "cardNumber"]
            );
        }
    }

    /**
     * TOKEN FALLBACK
     */

    if (
        !results.length &&
        retrievalTokens.length
    ) {
        const tokenFilter =
            buildOrFilter(
                USER_ITEM_NAME_FIELDS,
                retrievalTokens
            );

        if (tokenFilter) {
            const {
                data,
                error,
            } = await publicProvisionalUserItemsQuery()
                .or(tokenFilter)
                .limit(
                    MAX_USER_ITEM_RESULTS
                );

            if (error) {
                console.warn(
                    "[HobbliVision] User-item token retrieval failed:",
                    error
                );
            } else {
                addCandidates(
                    results,
                    seen,
                    data,
                    "user_item",
                    ["nameToken"]
                );
            }
        }
    }

    return results;
}

async function fetchUserItemsByCanonicalIdentity(
    names,
    retrievalTokens
) {
    const results = [];
    const seen = new Set();

    let canonicalFilter =
        buildOrFilter(
            CANONICAL_NAME_FIELDS,
            names
        );

    /**
     * If complete names fail, use meaningful tokens.
     */
    if (
        !canonicalFilter &&
        retrievalTokens.length
    ) {
        canonicalFilter =
            buildOrFilter(
                CANONICAL_NAME_FIELDS,
                retrievalTokens
            );
    }

    if (!canonicalFilter) {
        return results;
    }

    const {
        data: canonicalRows,
        error: canonicalError,
    } = await supabase
        .from("items")
        .select("id")
        .or(canonicalFilter)
        .limit(
            MAX_CANONICAL_RESULTS
        );

    if (canonicalError) {
        console.warn(
            "[HobbliVision] Canonical ID lookup for user-items failed:",
            canonicalError
        );

        return results;
    }

    const canonicalIds =
        (canonicalRows || [])
            .map(
                (item) => item?.id
            )
            .filter(Boolean);

    if (!canonicalIds.length) {
        return results;
    }

    const {
        data,
        error,
    } = await publicLinkedUserItemsQuery()
        .in(
            "item_id",
            canonicalIds
        )
        .limit(
            MAX_USER_ITEM_RESULTS
        );

    if (error) {
        console.warn(
            "[HobbliVision] User-item canonical retrieval failed:",
            error
        );
    } else {
        addCandidates(
            results,
            seen,
            data,
            "user_item",
            ["canonicalIdentity"]
        );
    }

    return results;
}

async function fetchUserItemFallbackCandidates(
    parsedOCR
) {
    const results = [];
    const seen = new Set();

    const year =
        getFallbackYear(parsedOCR);

    const set =
        getFallbackSet(parsedOCR);

    const cardNumber =
        getFallbackCardNumber(parsedOCR);

    /**
     * SET
     */

    if (set) {
        const {
            data,
            error,
        } = await publicProvisionalUserItemsQuery()
            .ilike(
                "user_specific_set_name",
                `%${escapeLike(set)}%`
            )
            .limit(
                MAX_USER_ITEM_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] User-item set fallback failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "user_item",
                ["set"]
            );
        }
    }

    /**
     * YEAR
     */

    if (year) {
        const {
            data,
            error,
        } = await publicProvisionalUserItemsQuery()
            .eq(
                "user_specific_year",
                year
            )
            .limit(
                MAX_USER_ITEM_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] User-item year fallback failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "user_item",
                ["year"]
            );
        }
    }

    /**
     * CARD NUMBER
     */

    if (cardNumber) {
        const {
            data,
            error,
        } = await publicProvisionalUserItemsQuery()
            .filter(
                "attributes->>card_number",
                "ilike",
                `%${escapeLike(cardNumber)}%`
            )
            .limit(
                MAX_USER_ITEM_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] User-item card-number fallback failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "user_item",
                ["cardNumber"]
            );
        }
    }

    return results;
}

async function fetchUserItemNameOnlyCandidates(
    names,
    retrievalTokens
) {
    const results = [];
    const seen = new Set();

    const nameFilter =
        buildOrFilter(
            USER_ITEM_NAME_FIELDS,
            names
        );

    if (nameFilter) {
        const {
            data,
            error,
        } = await publicProvisionalUserItemsQuery()
            .or(nameFilter)
            .limit(
                MAX_USER_ITEM_RESULTS
            );

        if (error) {
            console.warn(
                "[HobbliVision] User-item name-only retrieval failed:",
                error
            );
        } else {
            addCandidates(
                results,
                seen,
                data,
                "user_item",
                ["nameOnly"]
            );
        }
    }

    /**
     * Token fallback.
     */

    if (
        !results.length &&
        retrievalTokens.length
    ) {
        const tokenFilter =
            buildOrFilter(
                USER_ITEM_NAME_FIELDS,
                retrievalTokens
            );

        if (tokenFilter) {
            const {
                data,
                error,
            } = await publicProvisionalUserItemsQuery()
                .or(tokenFilter)
                .limit(
                    MAX_USER_ITEM_RESULTS
                );

            if (error) {
                console.warn(
                    "[HobbliVision] User-item token-only retrieval failed:",
                    error
                );
            } else {
                addCandidates(
                    results,
                    seen,
                    data,
                    "user_item",
                    ["nameToken"]
                );
            }
        }
    }

    return results;
}

async function fetchPublicUserItems(
    parsedOCR,
    names,
    retrievalTokens
) {
    const [
        identityCandidates,
        canonicalLinkedCandidates,
        fallbackCandidates,
        nameOnlyCandidates,
    ] = await Promise.all([
        fetchUserItemIdentityCandidates(
            parsedOCR,
            names,
            retrievalTokens
        ),

        fetchUserItemsByCanonicalIdentity(
            names,
            retrievalTokens
        ),

        fetchUserItemFallbackCandidates(
            parsedOCR
        ),

        fetchUserItemNameOnlyCandidates(
            names,
            retrievalTokens
        ),
    ]);

    const results = [];
    const seen = new Set();

    addCandidates(
        results,
        seen,
        identityCandidates,
        "user_item"
    );

    addCandidates(
        results,
        seen,
        canonicalLinkedCandidates,
        "user_item"
    );

    addCandidates(
        results,
        seen,
        fallbackCandidates,
        "user_item"
    );

    addCandidates(
        results,
        seen,
        nameOnlyCandidates,
        "user_item"
    );

    return results;
}

/**
 * ============================================================
 * DATABASE IMAGE RETRIEVAL
 * ============================================================
 */

async function attachCandidateImages(
    candidates
) {
    if (!candidates?.length) {
        return candidates;
    }

    let enrichedCandidates =
        candidates.map(
            (candidate) => ({
                ...candidate,

                databaseImages:
                    candidate.sourceType === "canonical" &&
                    resolveCanonicalStockImage(candidate)
                        ? [
                              {
                                  url: resolveCanonicalStockImage(candidate),

                                  position: 0,

                                  isMain: true,

                                  source:
                                      "canonical_r2_image",
                              },
                          ]
                        : [],
            })
        );

    const userItemIds =
        enrichedCandidates
            .filter(
                (candidate) =>
                    candidate.sourceType ===
                    "user_item"
            )
            .map(
                (candidate) =>
                    candidate.id
            )
            .filter(Boolean);

    if (!userItemIds.length) {
        return enrichedCandidates;
    }

    const {
        data: imageRows,
        error: imageError,
    } = await supabase
        .from("item_images")
        .select(
            "id, user_item_id, position, created_at, storage_provider, storage_bucket, object_key"
        )
        .in(
            "user_item_id",
            userItemIds
        )
        .order(
            "position",
            {
                ascending: true,
            }
        );

    if (imageError) {
        console.warn(
            "[HobbliVision] User-item image retrieval failed:",
            imageError
        );

        return enrichedCandidates;
    }

    const imagesByUserItem = {};

    for (const image of imageRows || []) {
        const imageUrl = resolveImageUrl(image);

        if (
            !image?.user_item_id ||
            !imageUrl
        ) {
            continue;
        }

        if (
            !imagesByUserItem[
                image.user_item_id
            ]
        ) {
            imagesByUserItem[
                image.user_item_id
            ] = [];
        }

        imagesByUserItem[
            image.user_item_id
        ].push({
            id: image.id,

            url: imageUrl,

            position: image.position,

            isMain:
                image.position === 0,

            source: "item_image",

            created_at:
                image.created_at,
        });
    }

    enrichedCandidates =
        enrichedCandidates.map(
            (candidate) => {
                if (
                    candidate.sourceType !==
                    "user_item"
                ) {
                    return candidate;
                }

                return {
                    ...candidate,

                    databaseImages:
                        imagesByUserItem[
                            candidate.id
                        ] || [],
                };
            }
        );

    console.log(
        "[HobbliVision] Candidate images attached:",
        enrichedCandidates.filter(
            (candidate) =>
                candidate.databaseImages
                    ?.length
        ).length
    );

    return enrichedCandidates;
}

/**
 * ============================================================
 * MERGE
 * ============================================================
 */

function mergeCandidates(
    canonicalCandidates,
    userItemCandidates
) {
    const merged = [];
    const seen = new Set();

    for (const candidate of [
        ...(canonicalCandidates || []),
        ...(userItemCandidates || []),
    ]) {
        if (!candidate?.id) {
            continue;
        }

        const key =
            candidate.sourceType ===
            "user_item"
                ? `user_item:${candidate.id}`
                : `canonical:${candidate.id}`;

        if (seen.has(key)) {
            continue;
        }

        seen.add(key);

        merged.push(candidate);
    }

    return merged;
}

/**
 * ============================================================
 * DATABASE VALUE EXTRACTION
 * ============================================================
 *
 * This converts database records into the generic resolver's
 * database universe.
 *
 * No entity-specific fuzzy matching occurs here.
 */

function getCanonicalNameValues(
    candidate
) {
    return [
        candidate?.name,
        candidate?.additional_details
            ?.Identifier,
        candidate?.additional_details
            ?.Team,
    ].filter(Boolean);
}

function getUserItemNameValues(
    candidate
) {
    const attributes =
        candidate?.attributes || {};

    return [
        candidate?.name,
        candidate?.item?.name,
        candidate?.user_specific_name,
        candidate?.description,
        attributes?.parallel,
    ].filter(Boolean);
}

function getCandidateNameValues(
    candidate
) {
    if (
        candidate?.sourceType ===
        "canonical"
    ) {
        return getCanonicalNameValues(
            candidate
        );
    }

    return getUserItemNameValues(
        candidate
    );
}

/**
 * ============================================================
 * RESOLVER OBSERVATIONS
 * ============================================================
 *
 * nameEvidence contains the useful upstream observations.
 *
 * We preserve their confidence and source information.
 */

function getNameResolverObservations(
    parsedOCR,
    nameEvidence
) {
    const observations = [];

    /**
     * Evidence observations first.
     */
    for (
        const evidence
        of nameEvidence?.evidence || []
    ) {
        if (!evidence?.value) {
            continue;
        }

        observations.push({
            value: evidence.value,

            confidence:
                Number(
                    evidence.confidence
                ) || 0.5,

            source:
                evidence.source ||
                "nameEvidence",

            supportCount:
                evidence.supportCount,

            directIdentity:
                Boolean(
                    evidence.directIdentity
                ),
        });
    }

    /**
     * Parser-level identity observations.
     */
    if (parsedOCR?.player) {
        observations.push({
            value: parsedOCR.player,

            confidence:
                Number(
                    parsedOCR.playerConfidence
                ) || 0.75,

            source: "parser.player",
        });
    }

    if (
        parsedOCR?.itemName &&
        parsedOCR.itemName !==
            parsedOCR.player
    ) {
        observations.push({
            value: parsedOCR.itemName,

            confidence:
                Number(
                    parsedOCR.itemNameConfidence
                ) || 0.75,

            source: "parser.itemName",
        });
    }

    /**
     * Raw name candidates.
     */
    for (
        const candidate
        of parsedOCR?.nameCandidates || []
    ) {
        const value =
            typeof candidate === "string"
                ? candidate
                : candidate?.value;

        if (!value) {
            continue;
        }

        observations.push({
            value,

            confidence:
                Number(
                    candidate?.confidence
                ) || 0.5,

            source:
                candidate?.source ||
                "parser.nameCandidate",
        });
    }

    return observations;
}

/**
 * ============================================================
 * RESOLVE NAME EVIDENCE AGAINST CANDIDATES
 * ============================================================
 *
 * This is the major architectural change.
 *
 * Before:
 *
 *   OCR → "resolved name" → retrieval
 *
 * Now:
 *
 *   OCR → retrieval aliases → database candidates
 *      → evidenceResolver → resolved identity
 *
 * This means the resolver finally has the database universe
 * it needs to do its job.
 */

function resolveCandidateNameEvidence(
    candidates,
    parsedOCR,
    nameEvidence
) {
    const observations =
        getNameResolverObservations(
            parsedOCR,
            nameEvidence
        );

    if (!observations.length) {
        return {
            candidates,
            resolvedNameEvidence: {
                type: "name",
                resolved: null,
                value: null,
                confidence: 0,
                observations: [],
                candidates: [],
                hasDatabaseMatch: false,
            },
        };
    }

    /**
     * Build the database universe from the candidate pool.
     *
     * Multiple records may have the same name. We preserve
     * frequency because the resolver can use it as supporting
     * evidence without allowing frequency to dominate identity.
     */
    const databaseValueMap =
        new Map();

    for (const candidate of candidates) {
        const values =
            getCandidateNameValues(
                candidate
            );

        for (const value of values) {
            const normalized =
                normalize(value);

            if (!normalized) {
                continue;
            }

            const existing =
                databaseValueMap.get(
                    normalized
                );

            if (existing) {
                existing.frequency += 1;
                continue;
            }

            databaseValueMap.set(
                normalized,
                {
                    value,
                    frequency: 1,
                }
            );
        }
    }

    const databaseValues =
        [...databaseValueMap.values()];

    const resolvedNameEvidence =
        resolveEntity({
            type: "name",

            observations,

            databaseValues,
        });

    /**
     * Attach the resolver comparison to every candidate.
     *
     * A candidate may contain multiple possible database name
     * fields, so we compare each field independently and retain
     * the strongest result.
     */
    const enrichedCandidates =
        candidates.map(
            (candidate) => {
                const candidateValues =
                    getCandidateNameValues(
                        candidate
                    );

                if (
                    !candidateValues.length
                ) {
                    return {
                        ...candidate,

                        evidenceResolution: {
                            name: null,
                        },
                    };
                }

                let bestMatch = null;

                for (
                    const candidateValue
                    of candidateValues
                ) {
                    const resolution =
                        resolveEntity({
                            type: "name",

                            observations,

                            databaseValues: [
                                {
                                    value:
                                        candidateValue,

                                    frequency: 1,
                                },
                            ],
                        });

                    const primary =
                        resolution?.primary;

                    if (
                        !primary
                    ) {
                        continue;
                    }

                    const result = {
                        value:
                            candidateValue,

                        score:
                            primary.score,

                        confidence:
                            resolution.confidence,

                        supportCount:
                            primary.supportCount,

                        bestObservation:
                            primary.bestObservation,

                        directIdentity:
                            primary.directIdentity,

                        resolved:
                            resolution.resolved,

                        alternatives:
                            resolution.alternatives ||
                            [],
                    };

                    if (
                        !bestMatch ||
                        result.score >
                            bestMatch.score
                    ) {
                        bestMatch = result;
                    }
                }

                return {
                    ...candidate,

                    evidenceResolution: {
                        name: bestMatch,
                    },
                };
            }
        );

    /**
     * Sort only by resolver evidence when useful for diagnostics.
     *
     * We do NOT reorder the candidate pool here.
     *
     * The matcher remains responsible for final ranking.
     */
    return {
        candidates:
            enrichedCandidates,

        resolvedNameEvidence,
    };
}

/**
 * ============================================================
 * RESOLVER DISCOVERY → SECOND RETRIEVAL IDENTITY
 * ============================================================
 *
 * The first resolver pass may discover a much better identity
 * than the OCR/parser supplied.
 *
 * Example:
 *
 *   OCR name:       LAHAT TET
 *   resolver:       LeBron James
 *   score:          0.466
 *
 * That discovered identity can now be fed back into retrieval.
 *
 * This does NOT establish the final match. It only determines
 * whether the resolver has discovered a sufficiently strong
 * identity to justify another database lookup.
 */
function getResolverRetrievalIdentity(resolvedNameEvidence) {
    const primary = resolvedNameEvidence?.primary;

    if (!primary?.value) {
        return null;
    }

    const score = Number(primary.score) || 0;

    if (
        score <
        MIN_RESOLVER_CANDIDATE_SCORE
    ) {
        return null;
    }

    const value =
        String(primary.value).trim();

    if (!isUsableRetrievalName(value)) {
        return null;
    }

    return {
        value,
        score,
        confidence:
            Number(
                resolvedNameEvidence?.confidence
            ) || 0,
        resolved:
            resolvedNameEvidence?.resolved ??
            null,
    };
}

/**
 * ============================================================
 * SECOND-PASS IDENTITY RETRIEVAL
 * ============================================================
 *
 * Retrieve candidates using identities discovered by the first
 * resolver pass.
 *
 * This is deliberately a separate pass rather than modifying
 * the original retrieval logic.
 */
async function fetchCandidatesForResolverIdentity(parsedOCR, resolverIdentity) {
    if (!resolverIdentity?.value) {
        return {
            canonicalCandidates: [],
            userItemCandidates: [],
            retrievalNames: [],
            retrievalTokens: [],
        };
    }

    const discoveredNames =
        getRetrievalNameVariants(
            resolverIdentity.value
        ).filter(
            (name) =>
                isUsableRetrievalName(name)
        );

    const retrievalTokens =
        getMeaningfulRetrievalTokens(
            discoveredNames
        );

    if (!discoveredNames.length) {
        return {
            canonicalCandidates: [],
            userItemCandidates: [],
            retrievalNames: [],
            retrievalTokens,
        };
    }

    console.log(
        "[HobbliVision] SECOND RETRIEVAL starting:",
        {
            resolverIdentity:
                resolverIdentity.value,
            score:
                resolverIdentity.score,
            confidence:
                resolverIdentity.confidence,
            retrievalNames:
                discoveredNames,
            retrievalTokens,
        }
    );

    const [
        canonicalCandidates,
        userItemCandidates,
    ] = await Promise.all([
        fetchCanonicalCandidates(
            parsedOCR,
            discoveredNames,
            retrievalTokens
        ),
        fetchPublicUserItems(
            parsedOCR,
            discoveredNames,
            retrievalTokens
        ),
    ]);

    return {
        canonicalCandidates,
        userItemCandidates,
        retrievalNames:
            discoveredNames,
        retrievalTokens,
    };
}

/**
 * ============================================================
 * DIAGNOSTIC CANDIDATE SUMMARY
 * ============================================================
 */

function buildCandidateDiagnostics(candidates) {
    return (candidates || []).map(
        (candidate) => ({
            id: candidate.id,
            sourceType: candidate.sourceType,
            name: candidate.name || candidate.user_specific_name || null,
            year: candidate.year ?? candidate.user_specific_year ?? null,
            set: candidate.set_name || candidate.user_specific_set_name || null,
            retrievalEvidence:
                candidate.retrievalEvidence ||
                [],

            nameResolution:
                candidate
                    .evidenceResolution
                    ?.name
                    ? {
                          value:
                              candidate
                                  .evidenceResolution
                                  .name
                                  .value,

                          score:
                              candidate
                                  .evidenceResolution
                                  .name
                                  .score,

                          confidence:
                              candidate
                                  .evidenceResolution
                                  .name
                                  .confidence,

                          supportCount:
                              candidate
                                  .evidenceResolution
                                  .name
                                  .supportCount,

                          bestObservation:
                              candidate
                                  .evidenceResolution
                                  .name
                                  .bestObservation,

                          resolved:
                              candidate
                                  .evidenceResolution
                                  .name
                                  .resolved,
                      }
                    : null,
        })
    );
}

/**
 * ============================================================
 * MAIN RETRIEVAL
 * ============================================================
 */

export async function fetchMatcherCandidates(
    parsedOCR,
    options = {}
) {
    const start =
        performance.now();

    /**
     * --------------------------------------------------------
     * 1. Build OCR/name evidence
     * --------------------------------------------------------
     *
     * This remains separate from the generic resolver.
     *
     * nameEvidence extracts observations.
     *
     * evidenceResolver resolves those observations against
     * actual database values later.
     */

    const nameEvidence =
        extractNameEvidence(
            parsedOCR
        );

    const retrievalNames =
        getRetrievalNames(
            parsedOCR,
            nameEvidence
        );

    const retrievalTokens =
        getMeaningfulRetrievalTokens(
            retrievalNames
        );

    /**
     * --------------------------------------------------------
     * 2. Retrieval clues
     * --------------------------------------------------------
     */

    const retrievalYear =
        getFallbackYear(parsedOCR);

    const retrievalSet =
        getFallbackSet(parsedOCR);

    const retrievalBrand =
        getBrand(parsedOCR);

    const retrievalCardNumber =
        getFallbackCardNumber(
            parsedOCR
        );

    console.log(
        "[HobbliVision] Retrieval identifiers:",
        {
            year: retrievalYear,

            set: retrievalSet,

            brand: retrievalBrand,

            cardNumber:
                retrievalCardNumber,
        }
    );

    console.log(
        "[HobbliVision] Name evidence:",
        nameEvidence
    );

    console.log(
        "[HobbliVision] Retrieval names:",
        retrievalNames
    );

    console.log(
        "[HobbliVision] Retrieval tokens:",
        retrievalTokens
    );

    /**
     * --------------------------------------------------------
     * 3. Retrieve candidates
     * --------------------------------------------------------
     */

    const [
        canonicalCandidates,
        userItemCandidates,
    ] = await Promise.all([
        fetchCanonicalCandidates(
            parsedOCR,
            retrievalNames,
            retrievalTokens
        ),

        fetchPublicUserItems(
            parsedOCR,
            retrievalNames,
            retrievalTokens
        ),
    ]);

    /**
     * --------------------------------------------------------
     * 4. Merge
     * --------------------------------------------------------
     */

    let candidates =
        mergeCandidates(
            canonicalCandidates,
            userItemCandidates
        );

    /**
     * --------------------------------------------------------
     * 5. Resolve OCR evidence against database values
     * --------------------------------------------------------
     */

    const resolution =
        resolveCandidateNameEvidence(
            candidates,
            parsedOCR,
            nameEvidence
        );

    candidates =
        resolution.candidates;

    const resolvedNameEvidence =
        resolution.resolvedNameEvidence;

    console.log(
        "[HobbliVision] Resolved name evidence:",
        {
            resolved:
                resolvedNameEvidence?.resolved,

            value:
                resolvedNameEvidence?.value,

            confidence:
                resolvedNameEvidence?.confidence,

            primary:
                resolvedNameEvidence?.primary,

            alternatives:
                resolvedNameEvidence?.alternatives ||
                [],

            databaseCandidateCount:
                resolvedNameEvidence
                    ?.candidates
                    ?.length || 0,
        }
    );

    /**
 * --------------------------------------------------------
 * 5B. SECOND RETRIEVAL FROM RESOLVER DISCOVERY
 * --------------------------------------------------------
 *
 * The first retrieval may not contain the correct identity.
 *
 * Example:
 *   OCR retrieval name:
 *       LAHAT TET
 *
 *   First resolver pass:
 *       primary = LeBron James
 *       score   = 0.466
 *
 * If the resolver has discovered a sufficiently strong
 * identity, retrieve again using that identity.
 *
 * This closes the loop:
 *   retrieval
 *      ↓
 *   resolver
 *      ↓
 *   identity discovery
 *      ↓
 *   retrieval again
 *      ↓
 *   resolver again
 */
const resolverIdentity =
    getResolverRetrievalIdentity(
        resolvedNameEvidence
    );

let secondPassResolution = null;

if (
    resolverIdentity &&
    resolverIdentity.score >=
        MIN_RETRIEVAL_IDENTITY_SCORE
) {
    console.log(
        "[HobbliVision] Resolver discovered retrieval identity:",
        resolverIdentity
    );

    const secondRetrieval =
        await fetchCandidatesForResolverIdentity(
            parsedOCR,
            resolverIdentity
        );

    console.log(
        "[HobbliVision] SECOND RETRIEVAL results:",
        {
            canonical:
                secondRetrieval
                    .canonicalCandidates
                    ?.length || 0,
            userItems:
                secondRetrieval
                    .userItemCandidates
                    ?.length || 0,
            retrievalNames:
                secondRetrieval
                    .retrievalNames,
            retrievalTokens:
                secondRetrieval
                    .retrievalTokens,
        }
    );

    /**
     * Merge the newly discovered candidates into the original
     * candidate universe.
     *
     * The original candidates are preserved.
     */
    const expandedCandidates =
        mergeCandidates(
            candidates,
            [
                ...(secondRetrieval
                    .canonicalCandidates || []),
                ...(secondRetrieval
                    .userItemCandidates || []),
            ]
        );

    console.log(
        "[HobbliVision] Candidate pool expanded:",
        {
            before:
                candidates.length,
            after:
                expandedCandidates.length,
            added:
                expandedCandidates.length -
                candidates.length,
        }
    );

    /**
     * Resolve AGAIN against the expanded database universe.
     *
     * This is the critical second half of the loop.
     */
    secondPassResolution =
        resolveCandidateNameEvidence(
            expandedCandidates,
            parsedOCR,
            nameEvidence
        );

    candidates =
        secondPassResolution.candidates;

    console.log(
        "[HobbliVision] SECOND RESOLVER PASS:",
        {
            resolved:
                secondPassResolution
                    .resolvedNameEvidence
                    ?.resolved,
            value:
                secondPassResolution
                    .resolvedNameEvidence
                    ?.value,
            confidence:
                secondPassResolution
                    .resolvedNameEvidence
                    ?.confidence,
            primary:
                secondPassResolution
                    .resolvedNameEvidence
                    ?.primary,
            alternatives:
                secondPassResolution
                    .resolvedNameEvidence
                    ?.alternatives || [],
            databaseCandidateCount:
                secondPassResolution
                    .resolvedNameEvidence
                    ?.candidates
                    ?.length || 0,
        }
    );
}

const finalResolvedNameEvidence =
    secondPassResolution
        ?.resolvedNameEvidence ||
    resolvedNameEvidence;

    /**
     * --------------------------------------------------------
     * 6. Attach database images
     * --------------------------------------------------------
     */

    candidates =
        await attachCandidateImages(
            candidates
        );

    /**
     * --------------------------------------------------------
     * 7. Clean internal bookkeeping
     * --------------------------------------------------------
     */

    candidates =
        cleanCandidates(
            candidates
        );

    const cleanCanonicalCandidates =
        cleanCandidates(
            candidates.filter(
                (candidate) =>
                    candidate.sourceType ===
                    "canonical"
            )
        );

    const cleanUserItemCandidates =
        cleanCandidates(
            candidates.filter(
                (candidate) =>
                    candidate.sourceType ===
                    "user_item"
            )
        );

    /**
     * --------------------------------------------------------
     * 8. Diagnostics
     * --------------------------------------------------------
     */

    const elapsed =
        Math.round(
            performance.now() -
                start
        );

    console.log(
        `[HobbliVision] Candidate retrieval completed in ${elapsed}ms`
    );

    console.log(
        "[HobbliVision] Canonical candidates:",
        cleanCanonicalCandidates.length
    );

    console.log(
        "[HobbliVision] User-item candidates:",
        cleanUserItemCandidates.length
    );

    console.log(
        "[HobbliVision] Combined candidates:",
        candidates.length
    );

    console.log(
        "[HobbliVision] Candidate resolution diagnostics:",
        buildCandidateDiagnostics(
            candidates
        )
    );

    /**
     * --------------------------------------------------------
     * 9. Return
     * --------------------------------------------------------
     */

    return {
        candidates,
        canonicalCandidates: cleanCanonicalCandidates,
        userItemCandidates: cleanUserItemCandidates,
        nameEvidence,
        resolvedNameEvidence: finalResolvedNameEvidence,
        retrievalClues: {
            year: retrievalYear,
            set: retrievalSet,
            brand: retrievalBrand,
            cardNumber: retrievalCardNumber,
        },
        retrievalNames,
        retrievalTokens,
    };
}

export default fetchMatcherCandidates;